/**
 * Reusable Next-Action Rail component (work order M6 Station 2a item 2).
 *
 * Renders a next_actions array (label, intent, target route) as a page's
 * primary action row per UX_SPEC action rules:
 * - One clear primary action, with secondary and quiet actions
 * - Border radius token --q-radius: 6px applied to all controls
 * - Disabled buttons carry aria-disabled="true" and visible reason text adjacent
 * - Standard navigation via target route or custom action handlers
 */

import { type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { NextActionOut } from "../api/portfolio";
import "../brand/components.css";

export type ActionIntent = "primary" | "secondary" | "quiet";

export interface NextActionItem {
  readonly code: string;
  readonly label: string;
  readonly intent: ActionIntent;
  readonly targetRoute: string;
  readonly method?: string;
  readonly href?: string;
  readonly targetType?: string | null;
  readonly targetId?: string | null;
}

export interface NextActionRailProps {
  /** The incoming next_actions array from the API response */
  readonly actions?: readonly (NextActionOut | NextActionItem)[];
  /** Custom click handlers keyed by action code (e.g. to open dialogs) */
  readonly handlers?: Partial<Record<string, () => void>>;
  /** Disabled reason text keyed by action code; causes button to render disabled with visible reason adjacent */
  readonly disabledReasons?: Partial<Record<string, string>> | undefined;
  /** Generic action callback fired when any action is triggered */
  readonly onAction?: (action: NextActionItem) => void;
  /** Optional extra CSS classes */
  readonly className?: string;
  /** Children or prefix elements if needed */
  readonly children?: ReactNode;
}

/** The record types the task list narrows to (api/tasks.ts TaskRecordType). */
const TASK_RECORD_TYPES: ReadonlySet<string> = new Set([
  "PROJECT",
  "CONTRACT",
  "ITP",
  "SCHEDULE",
  "FINDING",
]);

function formatActionCode(code: string): string {
  const lower = code.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function resolveNextAction(
  action: NextActionOut | NextActionItem,
  index = 0,
): NextActionItem {
  // If already resolved, return as-is
  if ("label" in action && "intent" in action && "targetRoute" in action) {
    return action;
  }
  const resolved = resolveByCode(action, index);
  // Wave Experience A: the server says which items are acts and which only navigate. A
  // link is never drawn in an act's tone, whatever the code's default.
  return action.actionable_by_me === false ? { ...resolved, intent: "quiet" } : resolved;
}

function resolveByCode(action: NextActionOut, index: number): NextActionItem {
  const { code, method, href } = action;
  const targetType = action.target_type ?? null;
  const targetId = action.target_id ?? null;

  switch (code) {
    case "REGISTER_PROJECT":
      return {
        code,
        label: "Register project",
        intent: index === 0 ? "primary" : "secondary",
        targetRoute: "/portfolio/projects",
        method,
        href,
        targetType,
        targetId,
      };
    case "REGISTER_CONTRACT":
      return {
        code,
        label: "Register contract",
        intent: index === 0 ? "primary" : "secondary",
        targetRoute: targetId ? `/portfolio/projects/${targetId}` : "/portfolio/projects",
        method,
        href,
        targetType,
        targetId,
      };
    case "APPROVE_PROJECT":
      return {
        code,
        label: "Review and approve project",
        intent: "primary",
        targetRoute: targetId ? `/portfolio/projects/${targetId}?act=approve` : "/tasks",
        method,
        href,
        targetType,
        targetId,
      };
    case "APPROVE_VENDOR":
      return {
        code,
        label: "Approve as vendor",
        intent: "secondary",
        targetRoute: "/portfolio/parties",
        method,
        href,
        targetType,
        targetId,
      };
    case "OPEN_PROJECT_HISTORY":
      return {
        code,
        label: "View project history",
        intent: "quiet",
        targetRoute: targetId ? `/portfolio/projects/${targetId}#project-history` : "/tasks",
        method,
        href,
        targetType,
        targetId,
      };
    case "ASSIGN_QC_ENGINEER":
      return {
        code,
        label: "Assign QC engineer",
        intent: index === 0 ? "primary" : "secondary",
        targetRoute:
          targetType === "CONTRACT" && targetId
            ? `/portfolio/contracts/${targetId}`
            : targetType === "PROJECT" && targetId
              ? `/portfolio/projects/${targetId}`
              : "/portfolio/projects",
        method,
        href,
        targetType,
        targetId,
      };
    case "AUTHOR_ITP":
      return {
        code,
        label: "Author inspection and test plan",
        intent: index === 0 ? "primary" : "secondary",
        targetRoute: "/itps",
        method,
        href,
        targetType,
        targetId,
      };
    case "ATTACH_DOCUMENT":
      return {
        code,
        label: "Attach document",
        intent: "secondary",
        targetRoute:
          targetType === "CONTRACT" && targetId
            ? `/portfolio/contracts/${targetId}`
            : targetType === "PROJECT" && targetId
              ? `/portfolio/projects/${targetId}`
              : "/portfolio/projects",
        method,
        href,
        targetType,
        targetId,
      };
    case "REGISTER_PARTY":
      return {
        code,
        label: "Register party",
        intent: "secondary",
        targetRoute: "/portfolio/parties",
        method,
        href,
        targetType,
        targetId,
      };
    case "GRANT_PERMISSION":
      return {
        code,
        label: "Grant permission",
        intent: "secondary",
        targetRoute: "/configuration",
        method,
        href,
        targetType,
        targetId,
      };
    case "OPEN_TASK_LIST":
      // Wave W2 (Mahmoud's UAT): on a record the action names the record, and the list
      // it opens is narrowed to that record's tasks, never everybody's.
      return {
        code,
        label: "View open tasks",
        intent: "quiet",
        targetRoute:
          targetType !== null && targetId !== null && TASK_RECORD_TYPES.has(targetType)
            ? `/tasks?record_type=${targetType}&record_id=${targetId}`
            : "/tasks",
        method,
        href,
        targetType,
        targetId,
      };
    default:
      return {
        code,
        label: formatActionCode(code),
        intent: index === 0 ? "primary" : "secondary",
        targetRoute: "/tasks",
        method,
        href,
        targetType,
        targetId,
      };
  }
}

/**
 * Codes the server may offer whose act has NO screen in this build (wave W1.5 sweep):
 * their route leads to a register that does not offer the act, so a button would be a
 * dead end. They render nothing until their screen exists (absence, never a dead button).
 */
export const NO_SURFACE_CODES: ReadonlySet<string> = new Set([
  "REGISTER_PARTY",
  "REGISTER_CONTRACT",
  "APPROVE_VENDOR",
  "GRANT_PERMISSION",
]);

/**
 * A rail item is DEAD when clicking it would do nothing: it has no handler and its route
 * is the page it already sits on (Mahmoud's walk, finding 3b: the project page's Attach
 * document navigated to the project page), or its act has no screen at all.
 */
export function isDeadAction(
  item: NextActionItem,
  pathname: string,
  handlers?: Partial<Record<string, () => void>>,
): boolean {
  if (handlers?.[item.code] !== undefined) {
    return false;
  }
  if (NO_SURFACE_CODES.has(item.code)) {
    return true;
  }
  const route = item.targetRoute.split(/[?#]/)[0] ?? item.targetRoute;
  const hasQueryOrHash = route !== item.targetRoute;
  return route === pathname && !hasQueryOrHash;
}

function buttonClassName(intent: ActionIntent): string {
  switch (intent) {
    case "primary":
      return "q-button q-mini--primary";
    case "quiet":
      return "q-button q-button--quiet";
    case "secondary":
    default:
      return "q-button";
  }
}

export function NextActionRail({
  actions,
  handlers,
  disabledReasons,
  onAction,
  className,
  children,
}: NextActionRailProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();

  if ((!actions || actions.length === 0) && !children) {
    return null;
  }

  // A dead control is never drawn: absence over a button that does nothing.
  const items = (actions ?? [])
    .map((action, idx) => resolveNextAction(action, idx))
    .filter((item) => !isDeadAction(item, location.pathname, handlers));

  return (
    <div
      className={
        className
          ? `q-actions-row q-next-action-rail ${className}`
          : "q-actions-row q-next-action-rail"
      }
      role="region"
      aria-label="Next actions"
      data-testid="next-action-rail"
    >
      {children}
      {items.map((item) => {
        const disabledReason = disabledReasons?.[item.code];
        const isDisabled = disabledReason !== undefined;
        const customHandler = handlers?.[item.code];
        const btnClass = buttonClassName(item.intent);
        const testId = `next-action-${item.code}`;
        const reasonTestId = `next-action-disabled-reason-${item.code}`;

        if (isDisabled) {
          return (
            <div
              key={item.code}
              className="q-next-action-disabled-wrap"
              style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
            >
              <button
                type="button"
                className={btnClass}
                disabled
                aria-disabled="true"
                data-testid={testId}
              >
                {item.label}
              </button>
              <span className="q-note" data-testid={reasonTestId}>
                {disabledReason}
              </span>
            </div>
          );
        }

        if (customHandler !== undefined) {
          return (
            <button
              key={item.code}
              type="button"
              className={btnClass}
              data-testid={testId}
              onClick={() => {
                customHandler();
                onAction?.(item);
              }}
            >
              {item.label}
            </button>
          );
        }

        if (onAction !== undefined) {
          return (
            <button
              key={item.code}
              type="button"
              className={btnClass}
              data-testid={testId}
              onClick={() => {
                onAction(item);
                void navigate(item.targetRoute);
              }}
            >
              {item.label}
            </button>
          );
        }

        return (
          <Link key={item.code} to={item.targetRoute} className={btnClass} data-testid={testId}>
            {item.label}
          </Link>
        );
      })}
    </div>
  );
}
